import { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail, fmtUsd, fmtNum } from "../lib/api";
import { useLang } from "../i18n";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader,
  AlertDialogFooter, AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "../components/ui/alert-dialog";
import { DollarSign, Percent, Lock, Send, Users, BadgeCheck, ShieldBan, History } from "lucide-react";
import { toast } from "sonner";

const StatCard = ({ icon: Icon, label, value, testId, accent = "text-white" }) => (
  <div data-testid={testId} className="bg-zinc-900 border border-zinc-800/60 rounded-2xl p-6">
    <div className="flex items-center gap-2 text-zinc-500 text-xs uppercase tracking-widest font-bold mb-3">
      <Icon size={14} /> {label}
    </div>
    <p className={`text-2xl font-black ${accent}`}>{value}</p>
  </div>
);

export default function AdminPanel() {
  const { t, lang } = useLang();
  const [stats, setStats] = useState(null);
  const [streamers, setStreamers] = useState([]);
  const [txs, setTxs] = useState([]);
  const [fee, setFee] = useState("");
  const [winPct, setWinPct] = useState("");
  const [losePct, setLosePct] = useState("");
  const [minFollowers, setMinFollowers] = useState("");
  const [minVideos, setMinVideos] = useState("");
  const [reqs, setReqs] = useState({});
  const [refundFans, setRefundFans] = useState([]);
  const [reasonMap, setReasonMap] = useState({});
  const [debates, setDebates] = useState([]);
  const [debReasonMap, setDebReasonMap] = useState({});
  const [disputes, setDisputes] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, st, tx, cfg, rf, db, dp] = await Promise.all([
        api.get("/admin/stats"), api.get("/admin/streamers"),
        api.get("/admin/transactions"), api.get("/admin/settings"),
        api.get("/admin/refund-fans"), api.get("/admin/debates"),
        api.get("/admin/disputes"),
      ]);
      setStats(s.data); setStreamers(st.data); setTxs(tx.data);
      setFee(String(cfg.data.platform_fee_percent));
      setWinPct(String(cfg.data.debate_winner_percent));
      setLosePct(String(cfg.data.debate_loser_percent));
      setMinFollowers(String(cfg.data.min_followers));
      setMinVideos(String(cfg.data.min_videos));
      setReqs(cfg.data.platform_requirements || {});
      setRefundFans(rf.data || []);
      setDebates(db.data || []);
      setDisputes(dp.data || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const approve = async (userId, verified) => {
    try {
      await api.post(`/admin/streamers/${userId}/approve`, { verified });
      toast.success(t("admin_updated"));
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const suspend = async (userId, suspended) => {
    try {
      await api.post(`/admin/streamers/${userId}/suspend`, { suspended });
      toast.success(t("admin_updated"));
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const refund = async (sessionId) => {
    try {
      await api.post("/admin/refund", {
        session_id: sessionId,
        streamer_rejected: reasonMap[sessionId] === "rejected",
      });
      toast.success(t("admin_refund_ok"));
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const unbanFan = async (fanId) => {
    try {
      await api.post(`/admin/fans/${fanId}/unban`);
      toast.success(t("admin_unban_ok"));
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const releaseDispute = async (sessionId) => {
    try {
      await api.post(`/admin/payments/${sessionId}/release`);
      toast.success(t("disp_released"));
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const refundDispute = async (sessionId) => {
    try {
      await api.post("/admin/refund", { session_id: sessionId, streamer_rejected: true });
      toast.success(t("admin_refund_ok"));
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const refundAllDebate = async (debateId) => {
    try {
      await api.post(`/admin/debates/${debateId}/refund-all`, {
        streamer_rejected: debReasonMap[debateId] === "rejected",
      });
      toast.success(t("admin_refund_all_ok"));
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const saveFee = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const pr = {};
      ["youtube", "kick"].forEach((p) => {
        pr[p] = {
          min_followers: parseInt(reqs[p]?.min_followers, 10) || 0,
          min_videos: parseInt(reqs[p]?.min_videos, 10) || 0,
        };
      });
      await api.put("/admin/settings", {
        platform_fee_percent: parseInt(fee, 10),
        debate_winner_percent: parseInt(winPct, 10),
        debate_loser_percent: parseInt(losePct, 10),
        min_followers: pr.youtube.min_followers,
        min_videos: pr.youtube.min_videos,
        platform_requirements: pr,
      });
      toast.success(t("admin_saved"));
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const escrowMap = {
    held: { label: t("escrow_held"), cls: "bg-amber-500/15 text-amber-400 border-amber-700/50" },
    released: { label: t("escrow_released"), cls: "bg-emerald-500/15 text-emerald-400 border-emerald-700/50" },
    pending: { label: t("escrow_pending"), cls: "bg-zinc-700/40 text-zinc-400 border-zinc-700" },
    refunded: { label: t("escrow_refunded"), cls: "bg-red-500/15 text-red-400 border-red-800/50" },
  };

  if (!stats) return <div className="p-16 text-center text-zinc-500">{t("loading")}</div>;

  return (
    <div data-testid="admin-panel" className="max-w-7xl mx-auto px-6 py-12 space-y-10">
      <div>
        <h1 className="text-4xl font-black tracking-tighter text-white">{t("admin_title")}</h1>
        <p className="text-sm text-zinc-400 mt-2">{t("admin_sub")}</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
        <StatCard icon={DollarSign} label={t("admin_volume")} value={fmtUsd(stats.total_volume_cents)} testId="admin-stat-volume" />
        <StatCard icon={Percent} label={t("admin_fees")} value={fmtUsd(stats.platform_fees_cents)} testId="admin-stat-fees" accent="text-emerald-400" />
        <StatCard icon={Lock} label={t("admin_held")} value={fmtUsd(stats.held_cents)} testId="admin-stat-held" accent="text-amber-400" />
        <StatCard icon={Send} label={t("admin_transferred")} value={fmtUsd(stats.transferred_cents)} testId="admin-stat-transferred" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
        <StatCard icon={Users} label={t("admin_streamers")} value={fmtNum(stats.streamers_count)} testId="admin-stat-streamers" />
        <StatCard icon={Users} label={t("admin_fans")} value={fmtNum(stats.fans_count)} testId="admin-stat-fans" />
        <StatCard icon={BadgeCheck} label={t("admin_verified")} value={fmtNum(stats.verified_channels)} testId="admin-stat-verified" />
        <StatCard icon={DollarSign} label={t("admin_paid")} value={fmtNum(stats.paid_count)} testId="admin-stat-paid" />
      </div>

      <Tabs defaultValue="streamers">
        <TabsList className="bg-zinc-900 border border-zinc-800/60">
          <TabsTrigger data-testid="admin-tab-streamers" value="streamers">{t("admin_tab_streamers")}</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-transactions" value="transactions">{t("admin_tab_tx")}</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-fans" value="fans">{t("admin_tab_fans")}</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-disputes" value="disputes">
            {t("admin_tab_disputes")}{disputes.length > 0 ? ` (${disputes.length})` : ""}
          </TabsTrigger>
          <TabsTrigger data-testid="admin-tab-debates" value="debates">{t("admin_tab_debates")}</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-settings" value="settings">{t("admin_tab_settings")}</TabsTrigger>
        </TabsList>

        <TabsContent value="streamers" className="mt-6 space-y-3">
          {streamers.map((s) => (
            <div key={s.user_id} data-testid="admin-streamer-row" className="bg-zinc-900 border border-zinc-800/60 rounded-2xl p-5 flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              <div className="min-w-0">
                <p className="font-semibold text-white truncate">{s.name} <span className="text-zinc-500 font-normal text-sm">{s.email}</span></p>
                <p className="text-xs text-zinc-500 mt-1 capitalize">
                  {s.channel.platform} · @{s.channel.handle} · {fmtNum(s.channel.followers)} {t("profile_followers")} · {s.connect ? t("admin_connect_yes") : t("admin_connect_no")}
                </p>
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-sm text-zinc-300">{fmtUsd(s.total_cents)} <span className="text-zinc-600">/</span> <span className="text-emerald-400">{fmtUsd(s.fees_cents)}</span></span>
                {s.channel.suspended ? (
                  <Badge variant="outline" className="bg-red-500/15 text-red-400 border-red-800/50 text-xs">{t("admin_suspended")}</Badge>
                ) : s.channel.verified ? (
                  <Badge variant="outline" className="bg-emerald-500/15 text-emerald-400 border-emerald-700/50 text-xs">{t("dash_verified")}</Badge>
                ) : (
                  <Badge variant="outline" className="bg-zinc-700/40 text-zinc-400 border-zinc-700 text-xs">{t("dash_not_verified")}</Badge>
                )}
                <Button data-testid="admin-approve-btn" size="sm" variant="outline"
                  className="rounded-full border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:text-white bg-transparent"
                  onClick={() => approve(s.user_id, !s.channel.verified)}>
                  {s.channel.verified ? t("admin_revoke") : t("admin_approve")}
                </Button>
                <Button data-testid="admin-suspend-btn" size="sm" variant="outline"
                  className="rounded-full border-red-900/60 text-red-400 hover:bg-red-950/40 hover:text-red-300 bg-transparent"
                  onClick={() => suspend(s.user_id, !s.channel.suspended)}>
                  {s.channel.suspended ? t("admin_unsuspend") : t("admin_suspend")}
                </Button>
              </div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="transactions" className="mt-6 space-y-3">
          {txs.length === 0 ? (
            <p className="text-sm text-zinc-500">{t("admin_empty_tx")}</p>
          ) : txs.map((tx) => {
            const e = escrowMap[tx.escrow_status] || escrowMap.pending;
            return (
              <div key={tx.session_id} data-testid="admin-tx-row" className="bg-zinc-900 border border-zinc-800/60 rounded-2xl p-5 flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-white truncate">{tx.tier_name} · {tx.fan_name} → {tx.streamer_name}</p>
                  <p className="text-xs text-zinc-500 mt-1">
                    {new Date(tx.created_at).toLocaleString(lang)} · {fmtUsd(tx.amount_cents)} ({fmtUsd(tx.platform_fee_cents)} {t("admin_col_fees").toLowerCase()})
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  {tx.payment_status === "paid" ? (
                    <Badge variant="outline" className={`${e.cls} text-xs`}>{e.label}</Badge>
                  ) : (
                    <Badge variant="outline" className="bg-zinc-800 text-zinc-400 border-zinc-700 text-xs">
                      {{ pending: t("st_pending"), failed: t("st_failed"), expired: t("st_expired"), refunded: t("escrow_refunded") }[tx.payment_status] || tx.payment_status}
                    </Badge>
                  )}
                  {tx.payment_status === "paid" && (
                    <div className="flex items-center gap-2">
                      <select data-testid="admin-refund-reason-select"
                        value={reasonMap[tx.session_id] || "not_done"}
                        onChange={(e) => setReasonMap((m) => ({ ...m, [tx.session_id]: e.target.value }))}
                        className="rounded-full bg-zinc-950 border border-zinc-800 text-zinc-300 text-xs px-3 py-1.5 max-w-[220px] focus:outline-none focus:border-emerald-700">
                        <option value="not_done">{t("admin_refund_reason_not_done")}</option>
                        <option value="rejected">{t("admin_refund_reason_rejected")}</option>
                      </select>
                      <Button data-testid="admin-refund-btn" size="sm" variant="outline"
                        className="rounded-full border-red-900/60 text-red-400 hover:bg-red-950/40 hover:text-red-300 bg-transparent"
                        onClick={() => refund(tx.session_id)}>
                        {t("admin_refund")}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </TabsContent>

        <TabsContent value="fans" className="mt-6 space-y-3">
          <p className="text-sm text-zinc-400">{t("admin_fans_desc")}</p>
          {refundFans.length === 0 ? (
            <p className="text-sm text-zinc-500">{t("admin_fans_empty")}</p>
          ) : refundFans.map((f) => (
            <div key={f.id} data-testid="admin-refund-fan-row" className="bg-zinc-900 border border-zinc-800/60 rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="min-w-0">
                <p className="font-semibold text-white truncate">{f.name} <span className="text-zinc-500 font-normal text-sm">{f.email}</span></p>
                <p className="text-xs text-zinc-500 mt-1">
                  <span className="text-red-400 font-bold">{f.refund_count}</span> {t("admin_fan_refunds")}
                </p>
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                {f.banned ? (
                  <Badge variant="outline" className="bg-red-500/15 text-red-400 border-red-800/50 text-xs flex items-center gap-1">
                    <ShieldBan size={12} /> {t("admin_suspended")}
                  </Badge>
                ) : (
                  <Badge variant="outline" className="bg-amber-500/15 text-amber-400 border-amber-700/50 text-xs">{f.refund_count}/3</Badge>
                )}
                <Button data-testid="admin-unban-btn" size="sm" variant="outline"
                  className="rounded-full border-emerald-800/60 text-emerald-400 hover:bg-emerald-950/40 hover:text-emerald-300 bg-transparent"
                  onClick={() => unbanFan(f.id)}>
                  {t("admin_unban")}
                </Button>
              </div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="disputes" className="mt-6 space-y-3">
          <p className="text-sm text-zinc-400">{t("disp_desc")}</p>
          {disputes.length === 0 ? (
            <p className="text-sm text-zinc-500">{t("disp_empty")}</p>
          ) : disputes.map((d) => (
            <div key={d.session_id} data-testid="admin-dispute-row" className="bg-zinc-900 border border-red-900/40 rounded-2xl p-5 flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              <div className="min-w-0">
                <p className="font-semibold text-white truncate">{d.tier_name} · {fmtUsd(d.amount_cents)}</p>
                <p className="text-xs text-zinc-500 mt-1">{d.fan_name} → {d.streamer_name}</p>
                {d.dispute_reason && <p className="text-xs text-red-300/80 italic mt-1">“{d.dispute_reason}”</p>}
                {d.proof_url && (
                  <a href={d.proof_url} target="_blank" rel="noopener noreferrer" data-testid="admin-dispute-proof"
                    className="inline-flex items-center gap-1 text-xs text-sky-400 hover:text-sky-300 mt-1">
                    <History size={12} /> {t("disp_proof")}
                  </a>
                )}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Button data-testid="admin-dispute-refund-btn" size="sm" variant="outline"
                  className="rounded-full border-red-900/60 text-red-400 hover:bg-red-950/40 hover:text-red-300 bg-transparent"
                  onClick={() => refundDispute(d.session_id)}>
                  {t("disp_refund")}
                </Button>
                <Button data-testid="admin-dispute-release-btn" size="sm" variant="outline"
                  className="rounded-full border-emerald-800/60 text-emerald-400 hover:bg-emerald-950/40 hover:text-emerald-300 bg-transparent"
                  onClick={() => releaseDispute(d.session_id)}>
                  {t("disp_release")}
                </Button>
              </div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="debates" className="mt-6 space-y-3">
          <p className="text-sm text-zinc-400">{t("admin_debates_desc")}</p>
          {debates.length === 0 ? (
            <p className="text-sm text-zinc-500">{t("admin_debates_empty")}</p>
          ) : debates.map((d) => {
            const st = escrowMap.pending;
            const statusCls = { accepted: "bg-emerald-500/15 text-emerald-400 border-emerald-700/50",
              completed: "bg-zinc-700/40 text-zinc-300 border-zinc-700",
              refunded: "bg-red-500/15 text-red-400 border-red-800/50",
              proposed: "bg-amber-500/15 text-amber-400 border-amber-700/50" }[d.status] || st.cls;
            return (
              <div key={d.id} data-testid="admin-debate-row" className="bg-zinc-900 border border-zinc-800/60 rounded-2xl p-5 flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                <div className="min-w-0">
                  <p className="font-semibold text-white truncate">{d.topic}</p>
                  <p className="text-xs text-zinc-500 mt-1">
                    {d.streamer_a_name} vs {d.streamer_b_name} · {t("admin_debate_pot")}: <span className="text-emerald-400 font-semibold">{fmtUsd(d.pot_cents)}</span> · {d.contributors} {t("admin_debate_contributors")}
                  </p>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  <Badge variant="outline" className={`${statusCls} text-xs capitalize`}>{d.status}</Badge>
                  {d.status === "accepted" && d.pot_cents > 0 && (
                    <>
                      <select data-testid="admin-debate-refund-reason"
                        value={debReasonMap[d.id] || "not_done"}
                        onChange={(e) => setDebReasonMap((m) => ({ ...m, [d.id]: e.target.value }))}
                        className="rounded-full bg-zinc-950 border border-zinc-800 text-zinc-300 text-xs px-3 py-1.5 max-w-[220px] focus:outline-none focus:border-emerald-700">
                        <option value="not_done">{t("admin_refund_reason_not_done")}</option>
                        <option value="rejected">{t("admin_refund_reason_rejected")}</option>
                      </select>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button data-testid="admin-refund-all-btn" size="sm" variant="outline"
                            className="rounded-full border-red-900/60 text-red-400 hover:bg-red-950/40 hover:text-red-300 bg-transparent">
                            <ShieldBan size={13} className="mr-1.5" /> {t("admin_refund_all")}
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent data-testid="admin-refund-all-dialog" className="bg-zinc-900 border-zinc-800">
                          <AlertDialogHeader>
                            <AlertDialogTitle className="text-white">{t("admin_refund_all_confirm_title")}</AlertDialogTitle>
                            <AlertDialogDescription className="text-zinc-400">
                              {t("admin_refund_all_confirm_desc")} — <span className="text-zinc-200 font-semibold">{d.topic}</span>
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel data-testid="admin-refund-all-cancel"
                              className="rounded-full border-zinc-700 text-zinc-300 hover:bg-zinc-800 bg-transparent">
                              {t("admin_cancel")}
                            </AlertDialogCancel>
                            <AlertDialogAction data-testid="admin-refund-all-confirm"
                              className="rounded-full bg-red-600 hover:bg-red-500 text-white"
                              onClick={() => refundAllDebate(d.id)}>
                              {t("admin_refund_all_confirm_btn")}
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </TabsContent>

        <TabsContent value="settings" className="mt-6">
          <form onSubmit={saveFee} className="bg-zinc-900 border border-zinc-800/60 rounded-2xl p-8 max-w-xl space-y-4">
            <label className="block text-sm font-semibold text-white">{t("admin_fee_label")}</label>
            <Input data-testid="admin-fee-input" type="number" min="1" max="90" required value={fee}
              onChange={(e) => setFee(e.target.value)} className="bg-zinc-950 border-zinc-800 text-white" />
            <p className="text-xs text-zinc-500">{t("admin_fee_desc")}</p>
            <div className="border-t border-zinc-800 pt-4 space-y-4">
              <label className="block text-sm font-semibold text-white">{t("admin_debate_split_label")}</label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <span className="text-xs text-zinc-500">{t("admin_debate_winner_label")}</span>
                  <Input data-testid="admin-debate-winner-input" type="number" min="1" max="98" required value={winPct}
                    onChange={(e) => setWinPct(e.target.value)} className="bg-zinc-950 border-zinc-800 text-white mt-1" />
                </div>
                <div>
                  <span className="text-xs text-zinc-500">{t("admin_debate_loser_label")}</span>
                  <Input data-testid="admin-debate-loser-input" type="number" min="0" max="98" required value={losePct}
                    onChange={(e) => setLosePct(e.target.value)} className="bg-zinc-950 border-zinc-800 text-white mt-1" />
                </div>
              </div>
              <p data-testid="admin-debate-platform-cut" className="text-xs text-zinc-500">
                {t("admin_debate_desc")} {Math.max(0, 100 - (parseInt(winPct, 10) || 0) - (parseInt(losePct, 10) || 0))}%
              </p>
            </div>
            <div className="border-t border-zinc-800 pt-4 space-y-3">
              <label className="block text-sm font-semibold text-white">{t("admin_req_label")}</label>
              <p className="text-xs text-zinc-500">{t("admin_req_desc")}</p>
              <div className="space-y-3">
                {[["youtube", "YouTube"], ["kick", "Kick"]].map(([p, label]) => (
                  <div key={p} className="grid grid-cols-[110px_1fr_1fr] gap-3 items-center">
                    <span className="text-sm font-semibold text-zinc-300">{label}</span>
                    <div>
                      <span className="text-[10px] text-zinc-500 uppercase tracking-wide">{t("admin_min_followers_label")}</span>
                      <Input data-testid={`admin-req-${p}-followers`} type="number" min="0" required
                        value={reqs[p]?.min_followers ?? ""}
                        onChange={(e) => setReqs((r) => ({ ...r, [p]: { ...r[p], min_followers: e.target.value } }))}
                        className="bg-zinc-950 border-zinc-800 text-white mt-1 h-9" />
                    </div>
                    <div>
                      <span className="text-[10px] text-zinc-500 uppercase tracking-wide">{t("admin_min_videos_label")}</span>
                      <Input data-testid={`admin-req-${p}-videos`} type="number" min="0" required
                        value={reqs[p]?.min_videos ?? ""}
                        onChange={(e) => setReqs((r) => ({ ...r, [p]: { ...r[p], min_videos: e.target.value } }))}
                        className="bg-zinc-950 border-zinc-800 text-white mt-1 h-9" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <Button data-testid="admin-save-settings-btn" type="submit" disabled={saving}
              className="rounded-full bg-emerald-600 hover:bg-emerald-500 text-white">
              {t("admin_save")}
            </Button>
          </form>
        </TabsContent>
      </Tabs>
    </div>
  );
}
